<!-- eslint-disable id-length -->
<script lang="ts" setup>
import {onMounted, onUnmounted, ref, watch} from 'vue';

/**
 * A sidebar tooltip that auto-dismisses after `autoDismissMs` and also
 * dismisses on tap — on itself or anywhere else on the page — emitting
 * `dismiss`. Built for the IPD-2517 legal-docs hint.
 */
const props = defineProps<{
  label: string;
  hide: boolean;
  autoDismissMs: number;
}>();

const emit = defineEmits<{
  /** Emitted when the tooltip dismisses (timer, hint tap, or outside tap). */
  dismiss: [];
}>();

const showing = ref<boolean>(false);
const position = ref<{x: number, y: number}>({x:0, y:0});
const anchor = ref<HTMLDivElement | null>(null);
let dismissTimer: ReturnType<typeof setTimeout> | null = null;
// True when the current hide was caused by a deliberate user action (timer
// expiry, hint tap, outside tap). False when the hide is a layout-driven
// side effect (drawer state change via the `hide` prop watch). The
// `@after-leave` callback uses this to decide whether to notify the parent.
let dismissRequested = false;

function dismiss(): void {
  if (!showing.value) {return;}
  showing.value = false;
  if (dismissTimer !== null) {
    clearTimeout(dismissTimer);
    dismissTimer = null;
  }
  // Wait for the CSS leave animation to finish (`onAfterLeave`) before
  // notifying the parent, so its reactive `v-if` unmount doesn't snap the
  // tooltip away mid-fade.
  dismissRequested = true;
}

function onAfterLeave(): void {
  // Only emit when the hide originated from a deliberate user action —
  // opening another sidebar drawer (which flips `props.hide` and hides the
  // tooltip for layout reasons) must NOT consume the legal-docs reminder.
  if (dismissRequested) {
    dismissRequested = false;
    emit('dismiss');
  }
}

function handleResize(): void {
  showing.value = false;
  // remove tooltip in first resize
  window.removeEventListener('resize', handleResize);
}

function handleOutsideTap(event: Event): void {
  // Only genuine user gestures dismiss the hint. Synthetic/programmatic clicks
  // (a deferred tour step, a toast auto-action, an overlay click) must not
  // consume it without the user actually acknowledging it.
  if (!event.isTrusted) {return;}
  const target = event.target as Node | null;
  // Closest ancestor with our tooltip class triggers dismiss-on-click via the
  // @click on the element; let that path handle it instead of double-firing.
  if (target instanceof HTMLElement && target.closest('.sidebar-tooltip')) {
    return;
  }
  dismiss();
}

onMounted(() => {
  if (anchor.value) {
    const rect = anchor.value.getBoundingClientRect();
    // Position to the left of the icon, but adjust dynamically
    // Using right positioning instead to prevent overlap
    position.value = {x: rect.left - 10, y: rect.top - 6};
    showing.value = true;
  }
  window.addEventListener('resize', handleResize);
  dismissTimer = setTimeout(dismiss, props.autoDismissMs);
  // Capture-phase listener so any in-app click dismisses before the click's
  // own handler fires (e.g. navigating to another route).
  document.addEventListener('click', handleOutsideTap, true);
  document.addEventListener('touchstart', handleOutsideTap, true);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  document.removeEventListener('click', handleOutsideTap, true);
  document.removeEventListener('touchstart', handleOutsideTap, true);
  if (dismissTimer !== null) {
    clearTimeout(dismissTimer);
    dismissTimer = null;
  }
});

watch(() => props.hide, () => {
  // Hide tooltip on any drawer state change (open or close) — this prevents
  // the tooltip from rendering at the wrong position after the drawer
  // collapses. Intentionally does NOT set `dismissRequested = true`: a
  // layout-driven hide is not a user dismissal. In practice every user
  // click on a sidebar drawer button has already triggered `dismiss()` via
  // the capture-phase outside-tap listener before this watch fires, so this
  // path is only reachable for programmatic state changes (e.g., another
  // component mutating `drawer-store`). Keeping the BE flag intact in that
  // case means the hint can still appear on the user's next login if they
  // never deliberately acknowledged it.
  showing.value = false;
});
</script>
<template>
  <div ref="anchor"/>
  <Transition name="tooltip" @after-leave="onAfterLeave">
    <Teleport v-if="showing" :to="anchor">
      <div
        class="sidebar-tooltip"
        :style="{top: `${position.y}px`, left: `${position.x}px`}"
        @click="dismiss()"
      >
        <span class="text-body-sm">{{ label }}</span>
      </div>
    </Teleport>
  </Transition>
</template>
<style scoped lang="scss">
.sidebar-tooltip {
  position: fixed;
  z-index: 100000;
  background: var(--Neutral-UI-neutral-ui-100);
  color: var(--Neutral-UI-neutral-ui-0);
  border-radius: var(--border-radius);
  padding: var(--Spacing-spacing-3) var(--Spacing-spacing-4);
  white-space: nowrap;
  transform: translateX(-100%);
  pointer-events: auto;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 100%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid var(--Neutral-UI-neutral-ui-100);
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
  }
}

.tooltip-enter-active, .tooltip-leave-active {
  transition: all .8s ease-in-out;
}

.tooltip-enter-from, .tooltip-leave-to {
  opacity: 0;
}

.tooltip-enter-to, .tooltip-leave-from {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .tooltip-enter-active, .tooltip-leave-active {
    transition: none;
  }
}
</style>
